<template>
  <div class="gallery-wrapper">
    <Transition name="fade">
      <div class="thumbnails">
        <div class="thumbnails__inner">
          <button
            v-for="(image, index) in images"
            :key="image.id"
            class="thumbnail"
            :class="{ 'thumbnail--active': selectedImage === index }"
            @click="goToDot(index)"
          >
            <img
              v-if="image.mediaContentType === 'VIDEO'"
              :src="image.previewImage.url"
              class="thumbnail__image"
            />
            <img v-else :src="image.thumbnail" class="thumbnail__image" />
          </button>
        </div>
      </div>
    </Transition>
    <!-- Viewport wraps the horizontal scroller only; lg+ inset mask hides next/prev bleed at snap edges -->
    <div class="gallery-viewport">
      <div ref="gallery" class="gallery" @scroll.passive="handleGalleryScroll">
        <div
          v-for="image in images"
          :key="image.id"
          ref="galleryItems"
          class="gallery__item"
        >
          <Media
            :images="image"
            :sources="image.sources"
            cover
            cover-position="center top"
            no-transition
            eager
            @click="handleImageClick(image)"
          />
          <Badge v-if="image.isEcomXL" theme="product">
            {{ modelWearSizeLabel }}
          </Badge>
        </div>
      </div>
    </div>

    <ToastMessage class="toast"> Tap to zoom </ToastMessage>

    <div
      class="next-prev"
      :class="{
        'next-prev--next-only': !showPrevButton && showNextButton,
        'next-prev--prev-only': showPrevButton && !showNextButton,
      }"
    >
      <button v-if="showPrevButton" class="prev" @click="goPrev">
        <i class="icon icon-next-right"></i>
      </button>
      <button v-if="showNextButton" class="next" @click="goNext">
        <i class="icon icon-next-right"></i>
      </button>
    </div>
  </div>

  <div class="dots-container">
    <div class="dots">
      <span
        v-for="(item, index) in images"
        :key="item.id"
        class="dot"
        :class="{ 'dot--active': intersectStatus[index] }"
      ></span>
    </div>
  </div>

  <ImageViewer
    v-if="isImageViewerOpen"
    :image="imageViewerImage"
    @close="isImageViewerOpen = false"
  />
</template>

<script>
import Media from "@/components/Media.vue";
import ImageViewer from "@/components/product-display/ImageViewer.vue";
import { mapState } from "vuex";
import { getProductImageSequence } from "@/helpers/imageSequences.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import ToastMessage from "@/components/ToastMessage.vue";
import Badge from "@/components/BadgeV2.vue";
import { getModelWearSizeLabel } from "@/helpers/modelWearSize.js";

export default {
  name: "ProductMediaGallery",
  components: {
    Media,
    ImageViewer,
    ToastMessage,
    Badge,
  },
  inject: ["mq"],
  data() {
    return {
      observers: [],
      intersectStatus: [],
      isImageViewerOpen: false,
      selectedImage: 0,
      galleryResizeObserver: null,
      galleryScrollLeft: 0,
      galleryMaxScrollLeft: 0,
      hasMovedFromStart: false,
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    showPrevButton() {
      return (
        this.hasMovedFromStart ||
        this.selectedImage > 0 ||
        this.galleryScrollLeft > 4
      );
    },
    modelWearSizeLabel() {
      return getModelWearSizeLabel(this.product?.handle);
    },
    showNextButton() {
      return (
        this.selectedImage < this.images.length - 1 ||
        this.galleryScrollLeft < this.galleryMaxScrollLeft - 4
      );
    },
    images() {
      const {
        useAltCampaignImages = false,
        ecomFirstOnPDP = false,
        ecomXLOnPDP = false,
      } = getFeatureToggles();
      const imageSequence = getProductImageSequence(
        useAltCampaignImages,
        ecomFirstOnPDP,
        this.product,
        ecomXLOnPDP
      );

      let usedAltCampaign = false;

      const media = imageSequence
        .map((sequence) => {
          let mediaItem;

          if (sequence.includes("Video")) {
            mediaItem = this.getFirstReference(
              this.product[sequence]?.references.edges
            );
          } else if (sequence === "altCampaignImages") {
            // Handle alt campaign images with the new reference structure
            mediaItem = this.product.altCampaignImages?.reference?.image;
            if (mediaItem) {
              usedAltCampaign = true;
            }
          } else if (sequence === "campaignImages") {
            // Only use campaignImages if we haven't already used altCampaignImages
            if (!usedAltCampaign) {
              mediaItem = this.getFirstReference(
                this.product[sequence]?.references?.edges
              )?.image;
            }
          } else if (sequence === "campaignSecondaryImages") {
            // Only use campaignSecondaryImages if we haven't used altCampaignImages
            if (!usedAltCampaign) {
              mediaItem = this.getFirstReference(
                this.product[sequence]?.references.edges
              )?.image;
            }
          } else {
            mediaItem = this.getFirstReference(
              this.product[sequence]?.references?.edges
            )?.image;
          }

          if (!mediaItem) {
            return null;
          }

          return {
            ...mediaItem,
            isEcomXL: sequence === "ecomXLImages",
          };
        })
        .filter((mediaItem) => mediaItem !== null);

      return media;
    },
  },
  mounted() {
    this.intersectStatus = Array(this.$refs.galleryItems.length).fill(false);

    // setup the observers for each gallery item
    this.$refs.galleryItems.forEach((item, index) => {
      this.setupObserver(item, index);
    });

    this.setupGalleryResizeObserver();

    // Make sure first index is the first slide
    setTimeout(() => {
      this.applyGallerySlideMetrics();
      this.$refs.gallery.scrollTo({ left: 0, behavior: "auto" });
    }, 1000);
  },
  beforeUnmount() {
    this.observers.forEach((observer) => observer.disconnect());
    if (this.galleryResizeObserver) {
      this.galleryResizeObserver.disconnect();
      this.galleryResizeObserver = null;
    }
  },
  methods: {
    setupObserver(element, index) {
      const options = {
        root: this.$refs.gallery,
        rootMargin: "0px",
        threshold: 0.9,
      };

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.intersectStatus[index] = true;
        } else {
          this.intersectStatus[index] = false;
        }
      }, options);

      observer.observe(element);
      this.observers.push(observer);
    },
    setupGalleryResizeObserver() {
      const gallery = this.$refs.gallery;
      if (!gallery) return;

      this.applyGallerySlideMetrics();

      if (typeof ResizeObserver === "undefined") {
        return;
      }

      this.galleryResizeObserver = new ResizeObserver(() => {
        this.applyGallerySlideMetrics();
        this.repositionGalleryScroll("auto");
      });
      this.galleryResizeObserver.observe(gallery);
    },
    applyGallerySlideMetrics() {
      const gallery = this.$refs.gallery;
      if (!gallery) return;

      /* Sub-pixel + scroll-snap rounding can expose a neighbour slide; sizing
       * uses ceil(...) and a lg+ fudge so each slide is slightly wider than the track. */
      const viewportPx =
        gallery.getBoundingClientRect().width || gallery.clientWidth;
      const w = Math.max(1, Math.ceil(viewportPx));
      const isDesktop = this.mq?.lgPlus;
      const slidePx = isDesktop
        ? w + 4
        : Math.max(1, Math.ceil((viewportPx || w) * 0.88));
      const gapPx = isDesktop ? 0 : 12;
      const stepPx = slidePx + gapPx;

      gallery.style.setProperty("--pdp-slide-px", `${slidePx}px`);
      gallery.style.setProperty("--pdp-slide-gap-px", `${gapPx}px`);
      gallery.style.setProperty("--pdp-step-px", `${stepPx}px`);
      this.galleryMaxScrollLeft = Math.max(
        0,
        gallery.scrollWidth - gallery.clientWidth
      );
      this.galleryScrollLeft = gallery.scrollLeft;
    },
    repositionGalleryScroll(behavior = "auto") {
      const gallery = this.$refs.gallery;
      if (!gallery) return;

      if (
        this.selectedImage === null ||
        this.selectedImage < 0 ||
        this.selectedImage >= this.images.length
      ) {
        return;
      }

      const stepPx = Math.round(this.getStepPx());
      gallery.scrollTo({
        left: this.selectedImage * stepPx,
        behavior,
      });
    },
    getStepPx() {
      const gallery = this.$refs.gallery;
      if (!gallery) return 0;

      const stepValue = parseFloat(
        getComputedStyle(gallery).getPropertyValue("--pdp-step-px")
      );

      if (!Number.isFinite(stepValue) || stepValue <= 0) {
        const cw = gallery.getBoundingClientRect().width || gallery.clientWidth;
        return Math.max(1, Math.ceil(cw));
      }

      return stepValue;
    },
    goToDot(index) {
      if (index < 0 || index >= this.images.length) return;

      this.selectedImage = index;
      this.hasMovedFromStart = index > 0;

      const gallery = this.$refs.gallery;
      if (!gallery) return;

      this.applyGallerySlideMetrics();
      const stepPx = Math.round(this.getStepPx());
      const targetLeft = this.selectedImage * stepPx;
      /* Smooth scroll often ends a fraction of a px short on snap tracks — the
       * previous slide then shows a strip on the left. Desktop: jump exact. */
      const behavior = this.mq?.lgPlus ? "auto" : "smooth";
      gallery.scrollTo({
        left: targetLeft,
        behavior,
      });

      if (this.mq?.lgPlus) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            gallery.scrollLeft = targetLeft;
          });
        });
      }
    },
    goPrev() {
      this.goToDot(this.selectedImage - 1);
    },
    goNext() {
      this.goToDot(this.selectedImage + 1);
    },
    handleGalleryScroll() {
      const gallery = this.$refs.gallery;
      if (!gallery) return;

      this.galleryScrollLeft = gallery.scrollLeft;
      this.galleryMaxScrollLeft = Math.max(
        0,
        gallery.scrollWidth - gallery.clientWidth
      );
      if (this.galleryScrollLeft > 4) {
        this.hasMovedFromStart = true;
      } else if (this.selectedImage === 0) {
        this.hasMovedFromStart = false;
      }
    },
    handleImageClick(image) {
      if (image.sources?.length > 0) {
        return;
      }

      this.imageViewerImage = {
        blurry: image.thumbnail,
        url: image.imageViewerDesktop,
      };

      this.isImageViewerOpen = true;
    },
    getFirstReference(references) {
      if (!references) return null;
      const nodes = references.map((reference) => reference.node);
      return nodes[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery {
  display: flex;
  gap: var(--pdp-slide-gap-px, 0px);
  overflow: auto;
  width: 100%;
  flex-flow: row nowrap;
  scroll-snap-type: x mandatory;
  position: relative;
  // Set from JS so each slide width === viewport width in px (avoids desktop sub-pixel bleed).
  --pdp-slide-px: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  scroll-padding-left: 0;
  scroll-padding-right: 0;
  padding-right: 0;

  @include respond-below(lg) {
    padding-right: 12px;
  }

  & > :deep(*) {
    // start aligns one full-width slide flush with the viewport; center snap
    // plus imperfect scroll positions caused a thin strip of the previous slide to show.
    scroll-snap-align: start;
    scroll-snap-stop: always;
    flex: 0 0 var(--pdp-slide-px, 100%);
    width: var(--pdp-slide-px, 100%);
    min-width: var(--pdp-slide-px, 100%);
    max-width: var(--pdp-slide-px, 100%);
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    isolation: isolate;
    background-color: $color-white;
    cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='none'%3E%3Cpath fill='%23000' d='M27 21h-6v6h-2v-6h-6v-2h6v-6h2v6h6v2Z'/%3E%3Crect width='39' height='39' x='.5' y='.5' stroke='%23070707' rx='19.5'/%3E%3C/svg%3E")
        20 20,
      pointer;
  }

  // Chrome/GPU decode artifacts can show 1px seams on some videos.
  // Use clipped transform-based overscan so we hide seams
  // without affecting layout height.
  :deep(.media video) {
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    transform: translate3d(0, -1px, 0);
    transform-origin: center top;
    backface-visibility: hidden;
    will-change: transform;
  }

  @include respond-above(lg) {
    /* Extra horizontal overscan inside clipped slide (Media .media is overflow:hidden) */
    & > :deep(*) :deep(.media--cover:not(.media--video) img) {
      width: calc(100% + 12px) !important;
      max-width: none !important;
      margin-left: -6px !important;
    }

    & > :deep(*) :deep(.media video) {
      width: calc(100% + 12px) !important;
      margin-left: -6px !important;
    }
  }

  :deep(.media) {
    background-color: $color-white;
  }
}

.gallery__item {
  :deep(.badge) {
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: 2;

    @include respond-above(md) {
      top: 12px;
      left: 12px;
    }
  }
}

.gallery-viewport {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 0;

  @include respond-above(lg) {
    /* Paint over sub-pixel scroll-snap bleed (neighbour slide edge) */
    box-shadow: inset 8px 0 0 $color-white, inset -8px 0 0 $color-white;
    pointer-events: none;

    .gallery {
      pointer-events: auto;
    }
  }
}

.gallery-wrapper {
  position: relative;
  display: flex;
  z-index: 0;

  .toast {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 30px;
    text-align: center;

    @include respond-above(md) {
      display: none;
    }
  }
}

.thumbnails {
  display: none;
  position: relative;
  top: 0px;
  left: 0px;
  z-index: 1;
  margin-right: 33px;

  @include respond-above(xl) {
    display: block;
  }

  &__inner {
    width: 90px;
  }

  .thumbnail {
    margin-bottom: 8px;
    background: none;
    outline: none;
    padding: 4px;
    border: 1px solid transparent;

    &__image {
      display: block;
      max-width: 100%;
      height: auto;
    }

    &--active {
      border: 1px solid $color-black;
      border-radius: 4px;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dots {
  display: none;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background-color: rgba(128, 128, 128, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 50px;

  & > .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    margin: 0 4px;
    transition: all 0.3s ease;

    &.dot--active {
      background-color: $color-black;
    }
  }
}

.dots-container {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}

.next-prev {
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  left: 0;
  right: 0;
  width: auto;
  padding: 0 20px;
  justify-content: space-between;

  @include respond-above(lg) {
    display: flex;
  }

  // When thumbnails are visible, offset controls so they sit over the main image
  // (90px rail + 33px margin-right from .thumbnails).
  @include respond-above(xl) {
    left: 123px;
  }

  button {
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $color-white;
    z-index: 10;
    width: 40px;
    height: 40px;
    border-radius: 50%;

    i {
      color: $color-black;
    }
  }

  .prev > i {
    margin-right: 2px;
    transform: rotate(180deg);
    display: inline-block;
  }

  .next > i {
    margin-left: 2px;
  }
}

.next-prev--next-only {
  justify-content: flex-end;
}

.next-prev--prev-only {
  justify-content: flex-start;
}
</style>
